import { useReportLoading } from '../../shared/ReportLead';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Typography, ThemeProvider, useTheme } from '@mui/material';
import { AiTwotoneMail } from 'react-icons/ai';
import { AppVarsContext } from '../../../context/AppVarsContext';
import ReportPerformanceOverview from '../../shared/ReportPerformanceOverview';
import { createReportSurfaceTheme, getReportPageSx, getReportSurfaceSx, analyticsColors } from '../../shared/report-design';
import {
    EmailGallerySection,
    EmailInsightsCard,
    EmailTableSection,
    formatEmailDate,
} from '../../shared/emailReportShared';
import { GlassLoadingState, formatReportDateRangeLabel } from '../../shared/reportStatusCards';

const safeNum = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(safeNum(value));
const formatPercent = (value) => `${safeNum(value).toFixed(1)}%`;

const normalizeRate = (value) => {
    const num = safeNum(value);
    if (num > 0 && num <= 1) return num * 100;
    return num;
};

const readPreviewHref = (row) => (
    row?.permalink_url
    || row?.archive_url
    || row?.campaign_url
    || row?.url
    || row?.raw?.permalink_url
    || row?.raw?.archive_url
    || row?.raw?.campaign_url
    || row?.raw?.url
    || ''
);

export default function ConstantContact(props) {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    useReportLoading(loading, props.setLoading);
    const [cid] = useState(sessionStorage.getItem('rid'));
    const brandTheme = useTheme();
    const theme = useMemo(() => createReportSurfaceTheme(brandTheme), [brandTheme]);
    const { appVars, appLoading } = useContext(AppVarsContext);
    const title = props?.title || 'Constant Contact';
    const dateRangeLabel = formatReportDateRangeLabel(sessionStorage.getItem('sDate'), sessionStorage.getItem('eDate'));

    const fetchCampaigns = useCallback(async () => {
        if (!appVars?.api_url || !cid) { setLoading(false); return; }
        setLoading(true);
        try {
            const res = await fetch(`${appVars.api_url}/constant-contact/campaigns`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    cid,
                    sdate: sessionStorage.getItem('sDate'),
                    edate: sessionStorage.getItem('eDate'),
                    status: 'Done',
                }),
                redirect: 'follow',
            });
            const data = await res.json();
            setCampaigns(Array.isArray(data) ? data : []);

        } catch (error) {
            console.error('Error fetching Constant Contact campaigns:', error);
            setCampaigns([]);
        } finally {
            setLoading(false);
        }
    }, [appVars?.api_url, cid]);

    useEffect(() => {
        if (!appLoading) {
            fetchCampaigns();
        }
    }, [appLoading, fetchCampaigns]);

    useEffect(() => {
        const handleStorageChange = () => {
            setTimeout(() => fetchCampaigns(), 100);
        };
        window.addEventListener('dc', handleStorageChange);
        return () => window.removeEventListener('dc', handleStorageChange);
    }, [fetchCampaigns]);

    const rows = useMemo(() => campaigns.map((row, index) => {
        const sends = safeNum(row.sends);
        const delivered = safeNum(row.delivered ?? Math.max(0, sends - safeNum(row.bounces)));
        const opens = safeNum(row.unique_opens ?? row.opens);
        const clicks = safeNum(row.unique_clicks ?? row.clicks);
        const bounces = safeNum(row.bounces);
        const unsubscribes = safeNum(row.unsubscribes);
        const spamReports = safeNum(row.spam_reports);
        const openRate = normalizeRate(row.open_rate) || (delivered ? (opens / delivered) * 100 : 0);
        const clickRate = normalizeRate(row.click_rate) || (delivered ? (clicks / delivered) * 100 : 0);
        const ctor = normalizeRate(row.ctor) || (opens ? (clicks / opens) * 100 : 0);
        const bounceRate = normalizeRate(row.bounce) || (sends ? (bounces / sends) * 100 : 0);
        const unsubRate = normalizeRate(row.unsubscribe) || (sends ? (unsubscribes / sends) * 100 : 0);
        return {
            id: row?.primary_activity_id || row?.campaign_activity_id || row?.id || `cc-email-${index}`,
            name: row.subject || row.name || 'Untitled Campaign',
            sendDate: row.sent_at || row.updated_at || row.created_at,
            previewText: row.preheader || row.preview_text || row.description || '',
            sends,
            delivered,
            opens,
            clicks,
            bounces,
            unsubscribes,
            spamReports,
            openRate,
            clickRate,
            ctor,
            bounceRate,
            unsubRate,
            raw: row,
        };
    }), [campaigns]);

    const summary = useMemo(() => {
        const totals = rows.reduce((acc, row) => {
            acc.sends += row.sends;
            acc.delivered += row.delivered;
            acc.opens += row.opens;
            acc.clicks += row.clicks;
            acc.bounces += row.bounces;
            acc.unsubscribes += row.unsubscribes;
            acc.spamReports += row.spamReports;
            return acc;
        }, {
            sends: 0,
            delivered: 0,
            opens: 0,
            clicks: 0,
            bounces: 0,
            unsubscribes: 0,
            spamReports: 0,
        });

        return {
            emails: rows.length,
            totals,
            openRate: totals.delivered ? (totals.opens / totals.delivered) * 100 : 0,
            clickRate: totals.delivered ? (totals.clicks / totals.delivered) * 100 : 0,
            ctor: totals.opens ? (totals.clicks / totals.opens) * 100 : 0,
            bounceRate: totals.sends ? (totals.bounces / totals.sends) * 100 : 0,
            unsubRate: totals.sends ? (totals.unsubscribes / totals.sends) * 100 : 0,
        };
    }, [rows]);

    const ranked = useMemo(() => ({
        topOpen: [...rows].sort((a, b) => b.openRate - a.openRate)[0] || null,
        topClick: [...rows].sort((a, b) => b.clickRate - a.clickRate)[0] || null,
        topCTOR: [...rows].sort((a, b) => b.ctor - a.ctor)[0] || null,
        topBounce: [...rows].sort((a, b) => b.bounceRate - a.bounceRate)[0] || null,
    }), [rows]);

    const insightItems = useMemo(() => {
        const items = [];

        if (ranked.topOpen) {
            items.push({
                tone: 'success',
                label: 'Top Open Rate',
                text: `${ranked.topOpen.name} led the range with a ${formatPercent(ranked.topOpen.openRate)} open rate.`,
            });
        }

        if (ranked.topClick) {
            items.push({
                tone: 'success',
                label: 'Top Click Rate',
                text: `${ranked.topClick.name} drove the strongest click rate at ${formatPercent(ranked.topClick.clickRate)}.`,
            });
        }

        if (ranked.topBounce && ranked.topBounce.bounceRate >= 5) {
            items.push({
                tone: 'error',
                label: 'Deliverability Risk',
                text: `${ranked.topBounce.name} bounced at ${formatPercent(ranked.topBounce.bounceRate)}, which is the clearest deliverability issue in the selected range.`,
            });
        }

        if (summary.unsubRate > 0.3) {
            items.push({
                tone: 'warning',
                label: 'Unsubscribe Pressure',
                text: `Unsubscribe rate is ${formatPercent(summary.unsubRate)}. The audience is still engaging, but fatigue or message fit needs a closer look.`,
            });
        }

        return items.slice(0, 4);
    }, [ranked.topBounce, ranked.topClick, ranked.topOpen, summary.unsubRate]);



    const galleryRows = useMemo(() => rows.map((row) => {
        const activityId = row.raw?.primary_activity_id || row.raw?.campaign_activity_id || row.id;
        const previewHref = readPreviewHref(row);
        return {
            id: row.id,
            name: row.name,
            sendDate: row.sendDate,
            previewText: row.previewText,
            previewHref,
            placeholder: 'CC',
            thumbRequest: activityId ? {
                endpoint: '/constant-contact/email/preview-thumbnail',
                body: { cid, campaign_activity_id: activityId },
            } : null,
            metricItems: [
                { label: 'Sent', value: formatNumber(row.sends) },
                { label: 'Opens', value: `${formatNumber(row.opens)} · ${formatPercent(row.openRate)}` },
                { label: 'Clicks', value: `${formatNumber(row.clicks)} · ${formatPercent(row.clickRate)}` },
                { label: 'CTOR', value: formatPercent(row.ctor) },
            ],
        };
    }), [cid, rows]);

    const sortedRows = useMemo(() => [...rows].sort((a, b) => {
        const aDate = new Date(a.sendDate).getTime() || 0;
        const bDate = new Date(b.sendDate).getTime() || 0;
        return bDate - aDate;
    }), [rows]);

    if (loading) {
        return (
            <ThemeProvider theme={theme}><Box sx={getReportPageSx(theme)}>
                <GlassLoadingState
                    icon={<AiTwotoneMail size={34} />}
                    title={`Loading ${title}`}
                    subtitle={dateRangeLabel ? `Pulling send, deliverability, and click activity for ${dateRangeLabel}.` : 'Pulling send, deliverability, and click activity for the selected range.'}
                />
            </Box></ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={theme}><Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={getReportPageSx(theme)}>
                {rows.length > 0 ? (
                    <Box sx={{ position: 'relative', display: 'grid', gap: 2 }}>
                        <ReportPerformanceOverview
                            eyebrow="Constant Contact"
                            title="From delivery to engagement"
                            description="Review delivery, reader engagement, and traffic across your email campaigns."

                            stages={[
                                { label: 'Delivery', description: 'Messages sent and successfully delivered.', metrics: [{ label: 'Sent', value: formatNumber(summary.totals.sends), detail: 'Messages sent in this period' }, { label: 'Delivered', value: formatNumber(summary.totals.delivered), detail: 'Successful deliveries' }] },
                                { label: 'Engagement', description: 'Opens and their share of delivered messages.', metrics: [{ label: 'Opens', value: formatNumber(summary.totals.opens), detail: 'Provider-reported opens' }, { label: 'Open rate', value: formatPercent(summary.openRate), detail: 'Opens / delivered' }] },
                                { label: 'Traffic', description: 'Click activity generated by your emails.', metrics: [{ label: 'Clicks', value: formatNumber(summary.totals.clicks), detail: 'Provider-reported clicks' }, { label: 'Click rate', value: formatPercent(summary.clickRate), detail: 'Clicks / delivered' }] }
                            ]}
                            supportMetrics={[{ label: 'Emails', value: formatNumber(summary.emails) }, { label: 'Click-to-open rate', value: formatPercent(summary.ctor) }, { label: 'Bounce rate', value: formatPercent(summary.bounceRate) }, { label: 'Unsubscribe rate', value: formatPercent(summary.unsubRate) }]}
                            highlights={[{ label: 'Highest open rate', title: ranked.topOpen?.name || 'No email data', value: formatPercent(ranked.topOpen?.openRate), detail: 'Within the selected emails' }, { label: 'Highest click rate', title: ranked.topClick?.name || 'No email data', value: formatPercent(ranked.topClick?.clickRate), detail: 'Within the selected emails' }]}
                        />

                        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', xl: '1.1fr 0.9fr' } }}>
                            <Card sx={getReportSurfaceSx(theme)}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                        Delivery and engagement
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45, mb: 1.5 }}>
                                        Delivery is measured against sends; opens and clicks against delivered messages.
                                    </Typography>

                                    <Box sx={{ display: 'grid', gap: 1.25 }}>
                                        {[
                                            { label: 'Delivered', value: summary.totals.delivered, denom: summary.totals.sends, color: analyticsColors.acquisition },
                                            { label: 'Opened', value: summary.totals.opens, denom: summary.totals.delivered || summary.totals.sends, color: analyticsColors.engagement },
                                            { label: 'Clicked', value: summary.totals.clicks, denom: summary.totals.delivered || summary.totals.sends, color: analyticsColors.outcome },
                                        ].map((item) => {
                                            const percent = item.denom ? (item.value / item.denom) * 100 : 0;
                                            return (
                                                <Box key={item.label} sx={{ display: 'grid', gap: 0.5 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                                        <Typography variant="body2">{item.label}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatNumber(item.value)} · {formatPercent(percent)}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ height: 8, borderRadius: 999, overflow: 'hidden', bgcolor: theme.palette.action.hover }}>
                                                        <Box sx={{ width: `${Math.min(percent, 100)}%`, height: '100%', background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)` }} />
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </CardContent>
                            </Card>

                            <EmailInsightsCard
                                title="What Needs Attention"
                                subtitle="Constant Contact-specific engagement and deliverability callouts."
                                items={insightItems}
                            />
                        </Box>

                        <EmailGallerySection
                            providerLabel="Constant Contact"
                            title="Emails In Range"
                            subtitle="Creative thumbnails for each sent email in the selected date range."
                            rows={galleryRows}
                        />

                        <EmailTableSection
                            title="Email Performance"
                            subtitle="Delivery and engagement detail for each Constant Contact email."
                            rows={sortedRows}
                            emptyMessage="No Constant Contact emails were found in the selected date range."
                            columns={[
                                {
                                    key: 'name',
                                    label: 'Email',
                                    render: (row) => (
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                {row.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatEmailDate(row.sendDate)}
                                            </Typography>
                                        </Box>
                                    ),
                                },
                                { key: 'sends', label: 'Sent', align: 'right', render: (row) => formatNumber(row.sends) },
                                { key: 'delivered', label: 'Delivered', align: 'right', render: (row) => formatNumber(row.delivered) },
                                { key: 'opens', label: 'Opens', align: 'right', render: (row) => formatNumber(row.opens) },
                                { key: 'clicks', label: 'Clicks', align: 'right', render: (row) => formatNumber(row.clicks) },
                                { key: 'openRate', label: 'Open Rate', align: 'right', render: (row) => formatPercent(row.openRate) },
                                { key: 'clickRate', label: 'Click Rate', align: 'right', render: (row) => formatPercent(row.clickRate) },
                                { key: 'ctor', label: 'CTOR', align: 'right', render: (row) => formatPercent(row.ctor) },
                                { key: 'bounceRate', label: 'Bounce', align: 'right', render: (row) => formatPercent(row.bounceRate) },
                                { key: 'unsubRate', label: 'Unsubs', align: 'right', render: (row) => formatPercent(row.unsubRate) },
                            ]}
                        />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            m: 'auto',
                            border: `1px solid ${theme.palette.primary.main}`,
                            borderRadius: 2,
                            background: theme.palette.background.paper,
                            alignItems: 'center',
                        }}
                    >
                        <Box display="flex" flexDirection="column" p={2} alignItems="center" justifyContent="center" width="100%" gap={1}>
                            <AiTwotoneMail size={70} color={theme.palette.primary.main} />
                            <Typography>No Emails</Typography>
                            <Typography>{sessionStorage.getItem('sDate')} and {sessionStorage.getItem('eDate')}</Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box></ThemeProvider>
    );
}
